import React, { Fragment, useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import Hotkeys from 'react-hot-keys';

import { GlobalState } from '../../../../store/reducers';
import {
    getAdmissionToIPDEnabled,
    getGenieSetting,
    getIsGrowthChartSuggestionsEnabled,
    getIsICDSuggestionsEnabled,
    getIsMilestoneEnabled,
    getIsSmartSuggestions,
    getIsVaccinationChartVisible,
    getPxElementConfig,
    getSectionNameConfig,
    getVitalsPadConfig,
} from '../../../../store/selectors/session';
import {
    upsertGenieSetting,
    upsertSectionNameConfig,
    upsertVitalsConfig,
} from '../../../../store/actions/session';
import { GeniePadElementsSettingItem, VitalsSelectedItemsType } from '../../../../api/pushGenie';
import { SlideFromRightWrapper } from '../../../common/SlideFromRightWrapper';
import ElementsConfigureTable from './padV2/ElementsConfigureTable';
import { Col, Modal, Row, Tabs } from 'antd';
import { EkaProIcon } from '../../../common/Icons';
import { track } from '../../../../tracking';
import {
    getPrescription,
    getPrintConfigs,
    getRxClinicId,
} from '../../../../store/selectors/prescriptionPad';
import { deleteVitals, updatePrintConfig } from '../../../../store/actions/prescriptionPad';
import { PrintConfigItemV2 } from '../../../../api/pushVisit';
import {
    MISC_KEYS,
    getItemFromMisc,
    getTemplateV2,
    getTemplates,
} from '../../../../store/idbHandler';
import { his } from '../../../../pages/history';
import { GO_TO_MY_RX_TEMPLATE } from '../../../../routes/paths';
import { SectionNameConfig } from '../../../../store/db';
import { AntTooltip } from '../../../common/AntTooltip';
import {
    GROWTH_CHART_RESULTS,
    GROWTH_CHART_VITALS,
    MilestoneUpdatedAt,
} from '../../../../constants';
import PediaModuleConfigure from './PediaModuleConfigure';
import PadConfigSettingsSection from './PadConfigSettingsSection';
import { milestoneDefaultDataVersion } from '../../../../api/getMilestone';
import { fetchMilestones } from '../../../../modules/milestone';
import { getMyDbId } from '../../../../api/mactiveid';
const { TabPane } = Tabs;

type ActiveTab = 'pad-order' | 'settings';

const tabs = [{ title: 'Rx pad' }, { title: 'Specialization', isNew: true }, { title: 'Settings' }];

export const ConfigurePadDialog: React.FC<{ closeMe: () => void }> = ({ closeMe }) => {
    const isSmartSuggestionsChecked = useSelector(getIsSmartSuggestions);
    const printConfigs = useSelector(getPrintConfigs);
    const rxClinicId = useSelector(getRxClinicId);
    const prescription = useSelector(getPrescription);
    const sectionNames = useSelector(getSectionNameConfig);
    const vitalConfig = useSelector(getVitalsPadConfig);

    const isPropertiesFocusSkipChecked: boolean = useSelector(
        (state: GlobalState) => getGenieSetting(state, 'propertiesFocusSkip') as boolean,
    );
    const isVaccinationChartVisible = useSelector(getIsVaccinationChartVisible);
    const isICDSuggestionsEnabled = useSelector(getIsICDSuggestionsEnabled);
    const isGrowthChartSuggestionsEnabled = useSelector(getIsGrowthChartSuggestionsEnabled);
    const isMilestoneChecked = useSelector(getIsMilestoneEnabled);
    const isAdmissionToIPDEnabled = useSelector(getAdmissionToIPDEnabled);
    const [isSmartSuggestions, setIsSmartSuggestions] = useState(false);
    const [isPropertiesFocusSkip, setIsPropertiesFocusSkip] = useState(false);
    const [showVaccinationChart, setIsShowVaccinationChart] = useState(false);
    const [showMilestone, setIsShowMilestone] = useState(false);
    const [showICDSuggestions, setShowICDSuggestions] = useState(false);
    const [showGrowthChart, setShowGrowthChart] = useState(false);
    const padElementsConfig = useSelector(getPxElementConfig);
    const [elementConfig, setPadConfig] = useState<GeniePadElementsSettingItem[]>(
        padElementsConfig || [],
    );
    const [sectionNameConfig, setSectionNameConfig] = useState<SectionNameConfig | undefined>(
        sectionNames,
    );
    const [editSectionId, setEditSectionId] = useState<string | undefined>(undefined);
    const [activeTab, setActiveTab] = useState<ActiveTab>('pad-order');
    const [admissionToIPDEnabled, setAdmissionToIPDEnabled] = useState(false);

    const dispatch = useDispatch();

    const rxDocId = useSelector(
        (state: GlobalState) => state.prescriptionPad.prescription?.doctor?._id,
    );

    useEffect(() => {
        setIsSmartSuggestions(isSmartSuggestionsChecked || false);
        setIsPropertiesFocusSkip(isPropertiesFocusSkipChecked || false);
        setIsShowVaccinationChart(isVaccinationChartVisible || false);
        setShowICDSuggestions(isICDSuggestionsEnabled || false);
        setShowGrowthChart(isGrowthChartSuggestionsEnabled || false);
        setIsShowMilestone(isMilestoneChecked || false);
        setAdmissionToIPDEnabled(isAdmissionToIPDEnabled || false);
    }, []);

    const onSave = async (): Promise<void> => {
        dispatch(
            upsertGenieSetting({
                padElements: elementConfig,
                smartSuggestions: isSmartSuggestions,
                propertiesFocusSkip: isPropertiesFocusSkip,
                showVaccinationChart: showVaccinationChart,
                icdSuggestion: showICDSuggestions,
                growthChart: showGrowthChart,
                showMilestone,
                admissionToIPDEnabled: admissionToIPDEnabled,
            }),
        );

        if (sectionNameConfig) {
            const sectionNames: SectionNameConfig = {};

            Object.keys(sectionNameConfig).forEach((key) => {
                if (sectionNameConfig[key]) {
                    sectionNames[key] = sectionNameConfig[key];
                }
            });

            dispatch(upsertSectionNameConfig(sectionNames, rxDocId || ''));
        }

        onIcdSymSuggestionStateChange(showICDSuggestions);
        onShowGrowthChartChange(showGrowthChart);
        onShowMilestoneChange(showMilestone);
        closeMe();
    };

    const onShowMilestoneChange = (showMilestone?: boolean) => {
        let newPadElementConfig: GeniePadElementsSettingItem[] = elementConfig;

        const restElements = elementConfig.filter((t) => t.id !== 'milestones');
        const doesMilestoneExist = elementConfig.find((t) => t.id === 'milestones')?.isShown;

        if (showMilestone) {
            if (!doesMilestoneExist) {
                newPadElementConfig = [
                    {
                        id: 'milestones',
                        name: 'Growth Milestones',
                        isShown: true,
                        isAutoCopy: false,
                    },
                    ...restElements,
                ];

                dispatch(
                    upsertGenieSetting({
                        padElements: newPadElementConfig,
                        showMilestone,
                    }),
                );
            }
        } else {
            if (doesMilestoneExist) {
                newPadElementConfig = [
                    ...elementConfig.filter((el) => el.id !== 'milestones'),
                    {
                        id: 'milestones',
                        name: 'Growth Milestones',
                        isShown: false,
                        isAutoCopy: false,
                    },
                ];

                dispatch(
                    upsertGenieSetting({
                        padElements: newPadElementConfig,
                        showMilestone,
                    }),
                );
            }
        }
    };

    const onShowGrowthChartChange = (showGrowthChart: boolean) => {
        if (showGrowthChart) {
            dispatch(deleteVitals('lb-6580967293'));
        } else {
            batch(() => {
                Object.keys(GROWTH_CHART_RESULTS).forEach((id) => {
                    dispatch(deleteVitals(id));
                });
            });
        }

        if (showGrowthChart) {
            const ofc = GROWTH_CHART_VITALS.find((gc) => gc.id === 'lb-7205446246');
            const bmi = GROWTH_CHART_VITALS.find((gc) => gc.id === 'lb-5305097546');
            const height = GROWTH_CHART_VITALS.find((gc) => gc.id === 'lb-7561569471');
            const weight = GROWTH_CHART_VITALS.find((gc) => gc.id === 'lb-7991006736');

            const finalResults: VitalsSelectedItemsType[] = [...(vitalConfig?.selectedItems || [])];

            const isWeightPresent = vitalConfig?.selectedItems?.find(
                (ele) => ele.id === weight?.id,
            );
            if (!isWeightPresent && weight) {
                finalResults.push(weight);
            }

            const isHeightPresent = vitalConfig?.selectedItems?.find(
                (ele) => ele.id === height?.id,
            );
            if (!isHeightPresent && height) {
                finalResults.push(height);
            }

            const isBmiPresent = vitalConfig?.selectedItems?.find((ele) => ele.id === bmi?.id);
            if (!isBmiPresent && bmi) {
                finalResults.push(bmi);
            }

            const isOfcPresent = vitalConfig?.selectedItems?.find((ele) => ele.id === ofc?.id);
            if (!isOfcPresent && ofc) {
                finalResults.push(ofc);
            }

            dispatch(
                upsertVitalsConfig({
                    selectedItems: finalResults,
                }),
            );
        }
    };

    const onIcdSymSuggestionStateChange = async (suggestionState: boolean): Promise<void> => {
        batch(() => {
            if (printConfigs) {
                if (suggestionState) {
                    dispatch(
                        updatePrintConfig({
                            ...printConfigs,
                            symptoms: {
                                ...(printConfigs?.symptoms as PrintConfigItemV2),
                                showICD: true,
                                hideName: false,
                            },
                            diagnosis: {
                                ...(printConfigs?.diagnosis as PrintConfigItemV2),
                                showICD: true,
                                hideName: false,
                            },
                        }),
                    );
                } else {
                    const symptomsProp = printConfigs?.symptoms as PrintConfigItemV2;
                    const diagnosisProp = printConfigs?.diagnosis as PrintConfigItemV2;

                    delete symptomsProp?.showICD;
                    delete symptomsProp?.hideName;

                    delete diagnosisProp?.showICD;
                    delete diagnosisProp?.hideName;

                    if (printConfigs) {
                        dispatch(
                            updatePrintConfig({
                                ...printConfigs,
                                symptoms: symptomsProp,
                                diagnosis: diagnosisProp,
                            }),
                        );
                    }
                }
            }
        });
    };

    const onSectionNameEditName = (sectionName: string, name: string) => {
        setSectionNameConfig((prevSection) => ({
            ...prevSection,
            [sectionName]: name,
        }));
    };

    const onSectionNameEdit = async (sectionName?: string) => {
        if (
            rxClinicId &&
            (await getTemplateV2(prescription?.doctor._id || '', rxClinicId, 'PRINT'))
        ) {
            setEditSectionId(sectionName);
        } else if (getTemplates()?.print) {
            const t = Modal.confirm({
                title:
                    'Editing the field name is NOT allowed in custom template. Please set your template from My Rx template section.',
                okText: null,
                cancelText: 'Close',
                okButtonProps: { className: 'hidden' },
                cancelButtonProps: { className: 'neutral-btn font-lato' },
                visible: true,
                keyboard: true,
                closable: false,
                onOk: undefined,
                onCancel: (args) => {
                    t.destroy();
                    return;
                },
                bodyStyle: { padding: '24px' },
            });
        } else {
            setEditSectionId(sectionName);
        }
    };

    const toggleVaccination = () => {
        track('TOGGLE_VACCINATION_CHART', {
            showVaccinationChart: !showVaccinationChart ? 'true' : 'false',
        });
        setIsShowVaccinationChart((prev) => !prev);
    };

    const toggleGrowth = () => {
        track('GROWTH_CHART_TOGGLE', {
            showGrowthChart: !showGrowthChart ? 'true' : 'false',
        });
        setShowGrowthChart((prev) => !prev);
    };

    const toggleSmartSuggestion = () => setIsSmartSuggestions((prev) => !prev);
    const toggleSkipProp = () => setIsPropertiesFocusSkip((prev) => !prev);
    const toggleICD = () => {
        track('ICD_SUGGESTION_TOGGLE', {
            showICDSuggestions: !showICDSuggestions ? 'true' : 'false',
        });
        setShowICDSuggestions((prev) => !prev);
    };
    const toggleMilestones = async () => {
        if (!showMilestone) {
            const lastUpdated = await getItemFromMisc<MilestoneUpdatedAt>(
                getMyDbId() || '',
                MISC_KEYS.milestonesLastUpdatedAt,
            );

            if (
                !lastUpdated.isoString ||
                !lastUpdated.version ||
                lastUpdated.version !== milestoneDefaultDataVersion
            ) {
                await fetchMilestones();
            }
        }

        track('MILESTONE_TOGGLE', {
            showMilestone: !showMilestone ? 'true' : 'false',
        });
        setIsShowMilestone((prev) => !prev);
    };

    const toggleIpd = () => {
        setAdmissionToIPDEnabled((prev) => !prev);
        track('ADMISSION_TO_IPD_ENABLED_TOGGLE', {
            admissionToIPDEnabled: !admissionToIPDEnabled ? 'true' : 'false',
        });
    };

    return (
        <Hotkeys keyName="esc" onKeyDown={closeMe}>
            <SlideFromRightWrapper closeMe={closeMe}>
                <div className="bg-darwin-neutral-50 flex flex-col h-full space-between">
                    <div className="flex items-center space-x-16 p-16 border-b border-darwin-neutral-100 cursor-pointer">
                        <div
                            onClick={closeMe}
                            className="flex items-center justify-center w-16 h-16"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 448 512"
                                className="w-16 h-16 fill-darwin-neutral-1000"
                            >
                                <path d="M447.1 256c0 13.25-10.76 24.01-24.01 24.01H83.9l132.7 126.6c9.625 9.156 9.969 24.41 .8125 33.94c-9.156 9.594-24.34 9.938-33.94 .8125l-176-168C2.695 268.9 .0078 262.6 .0078 256S2.695 243.2 7.445 238.6l176-168C193 61.51 208.2 61.85 217.4 71.45c9.156 9.5 8.812 24.75-.8125 33.94l-132.7 126.6h340.1C437.2 232 447.1 242.8 447.1 256z"></path>
                            </svg>
                        </div>
                        <p className="text-darwin-neutral-1000 heading-2 bold">
                            Configure your Pad
                        </p>
                    </div>
                    <div className="pt-0 p-16 flex flex-col flex-1 h-full overflow-auto">
                        <Tabs
                            size="small"
                            tabBarGutter={4}
                            tabBarStyle={{
                                padding: '0 8px',
                                paddingTop: '8px',
                                background: '#F4F6F9',
                                marginBottom: '0px',
                            }}
                            style={{
                                height: '100%',
                            }}
                            className="configure-pad-dialog-tabs"
                            defaultActiveKey={activeTab}
                            onChange={(tab: string) => setActiveTab(tab as ActiveTab)}
                        >
                            <TabPane
                                tab={<span className="px-8">Pad Order</span>}
                                key="pad-order"
                                className="overflow-auto"
                            >
                                <div className="p-16 rounded-8 bg-white mt-12">
                                    <p className="body-1 bold text-darwin-neutral-1000">
                                        Order of elements on pad
                                    </p>
                                    <div className="pt-16">
                                        <ElementsConfigureTable
                                            config={elementConfig}
                                            onUpdate={setPadConfig}
                                            alternateSectionNameConfig={sectionNameConfig}
                                            onSectionNameEditName={onSectionNameEditName}
                                            isSectionNameEdit={editSectionId}
                                            onSectionNameEdit={onSectionNameEdit}
                                            disableRowBasedOnKey="isShown"
                                            customColumnConfig={[
                                                {
                                                    title: 'Auto Copy to pad',
                                                    keyName: 'isAutoCopy',
                                                },
                                            ]}
                                        />
                                    </div>
                                </div>
                            </TabPane>
                            <TabPane tab={<span className="px-8">Settings</span>} key="settings">
                                <div className="space-y-12 mt-12">
                                    <Row gutter={12}>
                                        <Col span={12}>
                                            <div className="p-16 h-full rounded-8 bg-white flex items-start justify-between">
                                                <div className="space-y-8 w-80p">
                                                    <p className="body-1 bold text-darwin-neutral-1000">
                                                        Smart Suggestions
                                                    </p>
                                                    <p className="body-3 text-darwin-neutral-700">
                                                        Smart suggestions include all the
                                                        “associated” suggestions in the pad.
                                                    </p>
                                                </div>
                                                <div
                                                    onClick={toggleSmartSuggestion}
                                                    className="darwin-switch primary"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isSmartSuggestions}
                                                    />
                                                    <span className="slider" />
                                                </div>
                                            </div>
                                        </Col>
                                        <Col span={12}>
                                            <div className="p-16 h-full rounded-8 bg-white flex items-start justify-between">
                                                <div className="space-y-8 w-80p">
                                                    <p className="body-1 bold text-darwin-neutral-1000">
                                                        Skip Properties
                                                    </p>
                                                    <p className="body-3 text-darwin-neutral-700">
                                                        Cursor moves from selection to selection,
                                                        skipping properties
                                                    </p>
                                                </div>
                                                <div
                                                    onClick={toggleSkipProp}
                                                    className="darwin-switch primary"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isPropertiesFocusSkip}
                                                    />
                                                    <span className="slider" />
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row gutter={12}>
                                        <Col span={12}>
                                            <div className="p-16 h-full rounded-8 bg-white flex items-start justify-between">
                                                <div className="space-y-8 w-80p">
                                                    <p className="body-1 bold text-darwin-neutral-1000">
                                                        Vaccination Chart
                                                    </p>
                                                    <p className="flex body-3 text-darwin-neutral-700">
                                                        Vaccination Chart for Adults and Children's
                                                        vaccination history.
                                                    </p>
                                                </div>
                                                <div className="flex f-full flex-col justify-between align-end">
                                                    <div
                                                        onClick={toggleVaccination}
                                                        className="darwin-switch primary"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={showVaccinationChart}
                                                        />
                                                        <span className="slider" />
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col span={12}>
                                            <div className="p-16 h-full rounded-8 bg-white flex items-start justify-between">
                                                <div className="space-y-8 w-80p">
                                                    <p className="body-1 bold text-darwin-neutral-1000">
                                                        ICD 10 coded
                                                    </p>
                                                    <p className="flex body-3 text-darwin-neutral-700">
                                                        ICD-10 codes displayed for
                                                        symptoms/diagnosis with the option to print
                                                        on Rx.
                                                    </p>
                                                </div>
                                                <div className="flex f-full flex-col justify-between align-end">
                                                    <div
                                                        onClick={toggleICD}
                                                        className="darwin-switch primary"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={showICDSuggestions}
                                                        />
                                                        <span className="slider" />
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row gutter={12}>
                                        <Col span={12}>
                                            <div className="p-16 h-full rounded-8 bg-white flex items-start justify-between">
                                                <div className="space-y-8 w-80p">
                                                    <p className="body-1 bold text-darwin-neutral-1000">
                                                        Enable Growth Chart
                                                    </p>
                                                    <p className="flex body-3 text-darwin-neutral-700">
                                                        Track Vital Growth Metrics over time.
                                                    </p>
                                                </div>
                                                <div className="flex f-full flex-col justify-between align-end">
                                                    <div
                                                        onClick={toggleGrowth}
                                                        className="darwin-switch primary"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={showGrowthChart}
                                                        />
                                                        <span className="slider" />
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col span={12}>
                                            <div className="p-16 h-full rounded-8 bg-white flex items-start justify-between">
                                                <div className="space-y-8 w-80p">
                                                    <p className="body-1 bold text-darwin-neutral-1000">
                                                        Milestones
                                                    </p>
                                                    <p className="flex body-3 text-darwin-neutral-700">
                                                        Track Growth milestones over time
                                                    </p>
                                                </div>
                                                <div className="flex f-full flex-col justify-between align-end">
                                                    <div
                                                        onClick={toggleMilestones}
                                                        className="darwin-switch primary"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={showMilestone}
                                                        />
                                                        <span className="slider" />
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row gutter={12}>
                                        <Col span={12}>
                                            <div className="p-16 h-full rounded-8 bg-white flex items-start justify-between">
                                                <div className="space-y-8 w-80p">
                                                    <p className="body-1 bold text-darwin-neutral-1000">
                                                        Admission to IPD
                                                    </p>
                                                    <p className="flex body-3 text-darwin-neutral-700">
                                                        Advise IPD admission with options to add
                                                        procedures and detailed notes.
                                                    </p>
                                                    <div className="flex gap-8 items-center">
                                                        <div
                                                            style={{
                                                                height: 'fit-content',
                                                                width: 'fit-content',
                                                            }}
                                                            className="px-8 py-2 text-11 font-700 text-center body-3 bg-darwin-accent-medical-history-orange-800 rounded-4 text-white"
                                                        >
                                                            New
                                                        </div>
                                                        <EkaProIcon className="w-41 h-20" />
                                                    </div>
                                                </div>
                                                <div className="flex f-full flex-col justify-between align-end">
                                                    <div
                                                        onClick={toggleIpd}
                                                        className="darwin-switch primary"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={admissionToIPDEnabled}
                                                        />
                                                        <span className="slider" />
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            </TabPane>
                        </Tabs>
                    </div>

                    <div className="flex items-center justify-between px-16 py-8 space-x-8 border-t border-neutral-7">
                        <div>
                            <button onClick={closeMe} className="neutral-btn btn--small px-16 py-8">
                                <span className="w-full text-center">Cancel</span>
                            </button>
                        </div>
                        <div>
                            {editSectionId ? (
                                <AntTooltip content={'Please fill the required Section Name'}>
                                    <button
                                        className="primary-btn  px-12 py-8 btn btn--small space-x-8 opacity-50"
                                        disabled={true}
                                    >
                                        <span className="w-full text-center">Save</span>
                                    </button>
                                </AntTooltip>
                            ) : (
                                <button
                                    onClick={onSave}
                                    className="primary-btn  px-12 py-8 btn btn--small space-x-8"
                                >
                                    <span className="w-full text-center">Save</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </SlideFromRightWrapper>
        </Hotkeys>
    );
};
